import { create } from 'zustand';
import { Notification } from '../types/notification';
import { NotificationService } from '../services/notificationService';

interface NotificationState {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  lastFetched: Date | null;
  error: string | null;

  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  markAsRead: (ids?: number[]) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: number) => Promise<void>;
  addNotification: (notification: Notification) => void;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  lastFetched: null,
  error: null,

  fetchNotifications: async () => {
    set({ isLoading: true, error: null });
    try {
      const notifications = await NotificationService.getNotifications();
      const unreadCount = notifications.filter(n => !n.readAt).length;
      set({ notifications, unreadCount, lastFetched: new Date() });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      set({ error: 'Failed to fetch notifications' });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await NotificationService.getUnreadCount();
      set({ unreadCount: count });
    } catch (error) {
      // Silently fail - not critical
      console.error('Failed to fetch unread count:', error);
    }
  },

  markAsRead: async (ids?: number[]) => {
    try {
      await NotificationService.markAsRead(ids);

      set(state => ({
        notifications: state.notifications.map(n =>
          (!ids || ids.includes(n.id))
            ? { ...n, readAt: new Date().toISOString() }
            : n
        ),
        unreadCount: ids
          ? Math.max(0, state.unreadCount - ids.length)
          : 0,
      }));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  },

  markAllAsRead: async () => {
    try {
      await NotificationService.markAllAsRead();
      set(state => ({
        notifications: state.notifications.map(n => ({
          ...n,
          readAt: n.readAt || new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  },

  deleteNotification: async (id: number) => {
    try {
      await NotificationService.deleteNotification(id);
      set(state => {
        const notification = state.notifications.find(n => n.id === id);
        const wasUnread = notification && !notification.readAt;
        return {
          notifications: state.notifications.filter(n => n.id !== id),
          unreadCount: wasUnread ? state.unreadCount - 1 : state.unreadCount,
        };
      });
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  },

  addNotification: (notification: Notification) => {
    set(state => {
      // Check if notification already exists to avoid duplicates
      const exists = state.notifications.some(n => n.id === notification.id);
      if (exists) {
        return state;
      }
      return {
        notifications: [notification, ...state.notifications],
        unreadCount: state.unreadCount + 1,
      };
    });
  },

  reset: () => {
    set({
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      lastFetched: null,
      error: null,
    });
  },
}));

export default useNotificationStore;
